//@flow
import * as React from 'react';

import { themr } from 'react-css-themr';
import {
    Button,
    ButtonWithPopover,
    ButtonTypes
} from 'common/components/core/button';

import OverlayDialog from 'common/components/core/dialog/OverlayDialog';
import SingleEntityLoadingView from 'common/entity/generic/SingleEntityLoadingView';
import baseFormTheme from './BaseForm.scss';
import EntityFormField from './EntityFormField';
import EntityFieldHelper from 'common/entity/generic/EntityFieldHelper';
import DataUtils from 'common/utils/DataUtils';
import entityIds from 'common/tests/entity/entityIds.json';
import { ObserverProvider } from 'common/components/core/intersection_observer/ObserverFlag';
import { PopoverProvider } from 'common/components/core/popover/popOverFactory';

class EntityForm extends React.PureComponent {
    dialogRef: ?HTMLElement;
    constructor(props) {
        super(props);

        this.state = {
            errorMessage: null,
            entity: this.props.entity,
            fields: []
        };
        this.dialogRef = null;
    }

    componentDidUpdate(prevProps) {
        const { entity, fields, errorMessage } = this.props;

        if (prevProps.entity !== entity) {
            this.setState({ entity });
        }
        if (prevProps.fields !== fields) {
            const normalizedFields = EntityFieldHelper.getNormalizedFields({
                entity,
                fields
            });
            this.setState({ entity, fields: normalizedFields });
        }
        if (prevProps.errorMessage !== errorMessage) {
            this.setState({ errorMessage });
        }
    }

    //Ref for form container to hide popovers when it goes out of view
    ref = React.createRef();

    onChange = ({ field, entity, value }) => {
        let dependentUpdatedFields = {};
        if (!field.supportsOnBlur) {
            dependentUpdatedFields = this.getDependentUpdatedFields({
                field,
                entity
            });
        }

        this.handleEvent({ field, entity, value, dependentUpdatedFields });
    };

    onBlur = ({ field, entity, value }) => {
        let dependentUpdatedFields = {};
        if (field.supportsOnBlur) {
            dependentUpdatedFields = this.getDependentUpdatedFields({
                field,
                entity
            });
        }

        this.handleEvent({ field, entity, value, dependentUpdatedFields });
    };

    handleEvent = ({ field, entity, value, dependentUpdatedFields }) => {
        const updatedField = {};
        updatedField[field.accessor] = { ...field };

        updatedField[
            field.accessor
        ] = EntityFieldHelper.getNormalizedValidatedField({
            field: updatedField[field.accessor],
            entity,
            value
        });

        this.setState({
            entity,
            fields: {
                ...this.state.fields,
                ...updatedField,
                ...dependentUpdatedFields
            }
        });
    };

    getDependentUpdatedFields = ({ field, entity }) => {
        const { fields } = this.state;
        const dependentUpdatedFields = {};
        if (field.dependentAccessors) {
            for (let i = 0; i < field.dependentAccessors.length; ++i) {
                const dependentAccessor = field.dependentAccessors[i];
                let dependentField = { ...fields[dependentAccessor] };
                dependentField = EntityFieldHelper.getNormalizedValidatedField({
                    field: dependentField,
                    entity
                });

                if (dependentField.fetchInfo) {
                    dependentField.fetchInfo.response = null;
                    this.runFetcherForField({ field: dependentField, entity });
                }

                dependentUpdatedFields[dependentAccessor] = dependentField;
            }
        }
        return dependentUpdatedFields;
    };

    runFetcherForField = ({ field, entity }) => {
        const { getMappedProps } = this.props;

        field.fetchInfo
            .fetcher(
                field.fetchInfo.params({
                    field,
                    entity,
                    props: getMappedProps()
                })
            )
            .then(data => {
                const fetchedField = {};
                fetchedField[field.accessor] = { ...field };
                fetchedField[field.accessor].fetchInfo.response = data;
                this.setState({
                    entity,
                    fields: { ...this.state.fields, ...fetchedField }
                });
            });
    };

    clearErrorMessage = () => {
        // Clear message after 5 sec
        setTimeout(() => {
            this.setState({
                errorMessage: null
            });
        }, 5000);
    };

    onSaveClick = (...args) => {
        const { t } = this.props;
        const { entity } = this.state;

        if (this.validateForm()) {
            this.props.onSubmit &&
                this.props.onSubmit({ updatedEntity: entity }, ...args);
        } else {
            this.setState(
                {
                    errorMessage: t(
                        'validations:genericFormValdiationFailMessage'
                    )
                },
                this.clearErrorMessage
            );
        }
    };

    validateForm = () => {
        const { fields, entity } = this.state;
        const { getMappedProps, shouldAllowSave } = this.props;
        const {
            isValid,
            validatedFields
        } = EntityFieldHelper.getValidatedFields({ fields });

        // using hasValidationErrors / setAllValidationErrors on entity for fields which consits of subFields
        // this can be removed later if we add functionality in fields having subFields
        // the new refactored validation structure

        // its basically a fallback to exisiting flow validations
        const validatedEntity = entity.setAllValidationErrors();

        this.setState({ fields: validatedFields, entity: validatedEntity });
        return (
            !validatedEntity.hasValidationErrors({
                helpers: getMappedProps()
            }) &&
            isValid &&
            this.validateCustomFields() &&
            shouldAllowSave
        );
    };

    validateCustomFields = () => {
        const { settingsName, entitySettings } = this.props;
        const customFields = entitySettings.getIn([
            settingsName,
            'customFields'
        ]);
        const { fields } = this.state;
        const { customProperties: customFieldsField } = fields;

        return DataUtils.validateCustomProperties(
            customFields,
            customFieldsField.value
        );
    };

    renderForm = () => {
        const { theme } = this.props;
        return (
            <div className={theme.container}>
                {this.renderTitle()}
                {this.renderContent()}
                {this.renderActionButtons()}
            </div>
        );
    };

    getOverlayInternalDivRef = ref => {
        this.dialogRef = ref;
    };

    renderModalForm = () => {
        const {
            theme,
            t,
            isLoading,
            fetchResources,
            errorMessage,
            onDismiss
        } = this.props;
        return isLoading ? (
            <SingleEntityLoadingView
                t={t}
                theme={theme}
                fetchResources={fetchResources}
                dataFetchError={errorMessage}
                onDismiss={onDismiss}
            />
        ) : (
            <PopoverProvider value={{ appendTarget: this.dialogRef }}>
                <OverlayDialog
                    active
                    className={theme.baseOverlay}
                    dialogClassName={theme.baseDialog}
                    getOverlayInternalDivRef={this.getOverlayInternalDivRef}
                >
                    <div className={theme.outerContainer}>
                        <Button
                            buttonType={ButtonTypes.Icon}
                            circle
                            icon="cancel"
                            className={theme.closeBtn}
                            theme={theme}
                            onClick={onDismiss}
                            testId={entityIds.buttons.cancel}
                        />
                        {this.renderForm()}
                    </div>
                </OverlayDialog>
            </PopoverProvider>
        );
    };

    renderTitle = () => {
        const { theme, isEditing, t, name } = this.props;
        const titleText = isEditing
            ? t('editEntity', {
                  entityNameId: name
              })
            : t('addNewEntity', {
                  entityNameId: name
              });
        return (
            <div className={theme.titleSection}>
                <span className={theme.title}>{titleText}</span>
            </div>
        );
    };

    renderContent = () => {
        const { theme, order, getMappedProps, isEditing, t } = this.props;
        const { entity, fields } = this.state;
        const { ref } = this;
        return (
            <div className={theme.contentSection} ref={ref}>
                <ObserverProvider
                    className={theme.observerSection}
                    rootNode={ref.current}
                >
                    {order.map(accessor => {
                        const field = fields[accessor];
                        return field && !entity.isHidden(accessor) ? (
                            <EntityFormField
                                key={field.accessor}
                                isEditing={isEditing}
                                t={t}
                                theme={theme}
                                entity={entity}
                                field={field}
                                onChange={this.onChange}
                                onBlur={this.onBlur}
                                getMappedProps={getMappedProps}
                            />
                        ) : null;
                    })}
                </ObserverProvider>
            </div>
        );
    };

    renderActionButtons = () => {
        const { theme, t, name, isSaving, onDismiss } = this.props;
        const { errorMessage } = this.state;
        return (
            <div className={theme.buttonsContainer}>
                {onDismiss ? (
                    <Button
                        flat
                        semibold
                        uppercase
                        buttonType={ButtonTypes.Text}
                        label={t('dismissButton')}
                        onClick={onDismiss}
                        className={theme.dismissButton}
                        testId={entityIds.buttons.dismiss}
                    />
                ) : null}

                <ButtonWithPopover
                    buttonType={ButtonTypes.Solid}
                    color="dark-sky-blue"
                    theme={theme}
                    smallLoader
                    onClick={this.onSaveClick}
                    label={t('saveEntity', {
                        entityNameId: name
                    })}
                    isLoading={isSaving}
                    uppercase
                    semibold
                    isPopOverOpen={!!errorMessage}
                    popOverContent={errorMessage}
                    testId={entityIds.buttons.save}
                />
            </div>
        );
    };

    render() {
        const { onDismiss } = this.props;
        return onDismiss ? this.renderModalForm() : this.renderForm();
    }
}

const ThemedEntityForm = themr('EntityForm', baseFormTheme)(EntityForm);
export default ThemedEntityForm;
